import React, { useState, useEffect } from 'react';
import { getTicketsByOrgId, getTicketById, addTicketMessage } from '../../../api/ticketApi';
import '../../../styles/tickets.css';

const SEVERITY_META = {
  '1 - System Down':        { label: 'Critical', cls: 'sev-critical' },
  '2 - Significant Impact': { label: 'High',     cls: 'sev-high'    },
  '3 - Minor Impact':       { label: 'Medium',   cls: 'sev-medium'  },
  '4 - Informational Only': { label: 'Low',      cls: 'sev-low'     },
};

function getStatusCls(status) {
  if (status === 'Waiting for Support')  return 'waiting-support';
  if (status === 'Waiting for Customer') return 'waiting-customer';
  return 'closed';
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString('en-SG', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function SupportTicketHistory({ orgId, currentUser, onBack }) {
  const [tickets,  setTickets]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply,    setReply]    = useState('');
  const [sending,  setSending]  = useState(false);

  useEffect(() => { loadTickets(); }, [orgId]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await getTicketsByOrgId(orgId);
      setTickets(data);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (ticketId) => {
    try {
      const ticket = await getTicketById(ticketId);
      setSelected(ticket);
      setReply('');
    } catch (err) {
      console.error('Error loading ticket detail:', err);
    }
  };

  const handleSendReply = async () => {
    if (!reply.trim() || !selected) return;
    try {
      setSending(true);
      await addTicketMessage(
        selected.id,
        reply.trim(),
        'client',
        currentUser?.displayName || currentUser?.name || currentUser?.email || 'Admin',
      );
      const updated = await getTicketById(selected.id);
      setSelected(updated);
      setReply('');
      loadTickets();
    } catch (err) {
      console.error('Error sending reply:', err);
    } finally {
      setSending(false);
    }
  };

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selected) {
    const sevMeta = SEVERITY_META[selected.ticketSeverity] || { label: selected.ticketSeverity, cls: 'sev-low' };
    return (
      <div className="bb-ticket-history">
        <div className="bb-ticket-history-header">
          <h2>Ticket Details</h2>
          <button className="bb-ticket-back-btn" onClick={() => setSelected(null)}>
            ← Back to Tickets
          </button>
        </div>

        <div className="bb-ticket-detail">
          <div className="bb-ticket-detail-top">
            <h3>{selected.ticketHeader}</h3>
            <span className={`bb-ticket-status ${getStatusCls(selected.ticketStatus)}`}>
              {selected.ticketStatus}
            </span>
          </div>

          <div className="bb-ticket-detail-meta">
            {[
              { label: 'Severity',     value: <span className={`bb-ticket-severity ${sevMeta.cls}`}>{sevMeta.label}</span> },
              { label: 'Type',         value: selected.ticketType },
              { label: 'Date Created', value: formatDate(selected.dateCreated) },
              { label: 'Last Updated', value: formatDate(selected.dateUpdatedClient || selected.dateUpdatedSupport) },
            ].map(f => (
              <div key={f.label} className="bb-ticket-meta-field">
                <label>{f.label}</label>
                <span>{f.value}</span>
              </div>
            ))}
          </div>

          <div className="bb-ticket-detail-desc">
            <h4>Description</h4>
            <p>{selected.ticketDescription}</p>
          </div>

          <div className="bb-ticket-messages-section">
            <h4>Message Log</h4>
            <div className="bb-ticket-messages">
              {(selected.messages || []).map((msg, i) => (
                <div key={i} className={`bb-ticket-message ${msg.sender}`}>
                  <div className="bb-ticket-msg-sender">{msg.senderName}</div>
                  <div className="bb-ticket-msg-text">{msg.message}</div>
                  <div className="bb-ticket-msg-time">{formatDate(msg.timestamp)}</div>
                </div>
              ))}
              {(selected.messages || []).length === 0 && (
                <p style={{ color: '#94A3B8', fontSize: 13, textAlign: 'center', margin: '8px 0' }}>No messages yet.</p>
              )}
            </div>

            {selected.ticketStatus !== 'Closed' && (
              <div className="bb-ticket-reply-area">
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Type your reply…"
                />
                <button
                  className="bb-ticket-btn-primary"
                  onClick={handleSendReply}
                  disabled={sending || !reply.trim()}
                >
                  {sending ? 'Sending…' : 'Send Reply'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="bb-ticket-history">
      <div className="bb-ticket-history-header">
        <h2>My Support Tickets</h2>
        <button className="bb-ticket-back-btn" onClick={onBack}>← Back to Modules</button>
      </div>

      {loading ? (
        <div className="bb-ticket-loading">Loading tickets…</div>
      ) : tickets.length === 0 ? (
        <div className="bb-ticket-empty">
          <p>No support tickets found.</p>
          <p>Submit a new ticket from the modules page if you need assistance.</p>
        </div>
      ) : (
        <table className="bb-ticket-table">
          <thead>
            <tr>
              <th>Ticket Header</th>
              <th>Status</th>
              <th>Severity</th>
              <th>Type</th>
              <th>Date Created</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(ticket => {
              const sevMeta = SEVERITY_META[ticket.ticketSeverity] || { label: ticket.ticketSeverity, cls: 'sev-low' };
              return (
                <tr key={ticket.id}>
                  <td>{ticket.ticketHeader}</td>
                  <td>
                    <span className={`bb-ticket-status ${getStatusCls(ticket.ticketStatus)}`}>
                      {ticket.ticketStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`bb-ticket-severity ${sevMeta.cls}`}>{sevMeta.label}</span>
                  </td>
                  <td>{ticket.ticketType}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(ticket.dateCreated)}</td>
                  <td>
                    <button className="bb-ticket-view-btn" onClick={() => handleView(ticket.id)}>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default SupportTicketHistory;
